import { useEffect, useRef } from "react";

const MIN_BLUR_MS = 500;

export function useContentProtection(contentSelector?: string) {
  const blurTargetRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const preventContext = (e: Event) => e.preventDefault();
    const preventSelect = (e: Event) => e.preventDefault();

    document.addEventListener("contextmenu", preventContext);
    document.addEventListener("selectstart", preventSelect);

    return () => {
      document.removeEventListener("contextmenu", preventContext);
      document.removeEventListener("selectstart", preventSelect);
    };
  }, []);

  useEffect(() => {
    function getTarget(): HTMLElement | null {
      if (contentSelector) {
        return document.querySelector(contentSelector);
      }
      return document.getElementById("main-content") || document.body;
    }

    let blurAppliedAt: number | null = null;

    function applyBlur() {
      const target = getTarget();
      if (target) {
        target.style.transition = "none";
        target.style.filter = "blur(8px)";
        blurTargetRef.current = target;
        blurAppliedAt = Date.now();
      }
    }

    function removeBlur() {
      if (blurTargetRef.current) {
        blurTargetRef.current.style.transition = "filter 0.2s ease";
        blurTargetRef.current.style.filter = "";
        blurTargetRef.current = null;
      }
      blurAppliedAt = null;
    }

    function handleVisibility() {
      if (document.visibilityState === "hidden") {
        applyBlur();
      } else {
        removeBlur();
      }
    }

    let blurTimer: ReturnType<typeof setTimeout> | null = null;
    let removeTimer: ReturnType<typeof setTimeout> | null = null;

    function hasVdoCipherPlayer(): boolean {
      return document.querySelector('iframe[src*="player.vdocipher.com"]') !== null;
    }

    function handleWindowBlur() {
      if (blurTimer !== null) {
        clearTimeout(blurTimer);
      }
      const delay = hasVdoCipherPlayer() ? 150 : 0;
      if (delay === 0) {
        if (!document.hasFocus()) applyBlur();
        return;
      }
      blurTimer = setTimeout(() => {
        if (!document.hasFocus()) {
          applyBlur();
        }
      }, delay);
    }

    function handleWindowFocus() {
      if (blurTimer !== null) {
        clearTimeout(blurTimer);
        blurTimer = null;
      }
      if (removeTimer !== null) {
        clearTimeout(removeTimer);
        removeTimer = null;
      }
      if (blurAppliedAt !== null) {
        const elapsed = Date.now() - blurAppliedAt;
        const remaining = MIN_BLUR_MS - elapsed;
        if (remaining > 0) {
          removeTimer = setTimeout(removeBlur, remaining);
          return;
        }
      }
      removeBlur();
    }

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      if (blurTimer !== null) clearTimeout(blurTimer);
      if (removeTimer !== null) clearTimeout(removeTimer);
      removeBlur();
    };
  }, [contentSelector]);
}
